import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './LikedList.module.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { X, Play } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useUnlikeItem, useUserLikes } from '../../hooks/useLikesHooks';
import Loading from '../../common/HousGlass Loader/Loading';

const LikedListCard = ({ item, onRemove }) => {
  const navigate = useNavigate();

  const handleImageError = (e) => {
    e.target.src = "/api/placeholder/400/240";
  };

  const handleClick = () => {
    if (item.item_type === "ebook") {
      navigate(`/book-detail/${item.slug}`);
    } else if (item.item_type === "audio") {
      navigate(`/audio-play/${item.slug}`);
    } else {
      navigate(`/video/${item.slug}`);
    }
  };

  return (
    <div className={styles.card} onClick={handleClick}>
      <img
        src={item.image_url}
        alt={item.item_type}
        className={`${styles.cardImage} ${styles[item.item_type]}`}
        onError={handleImageError}
      />
      {(item.item_type === "shorts" || item.item_type === "video") && (
        <div className={styles.playButton}>
          <Play size={20} />
        </div>
      )}
      <button
        className={styles.removeButton}
        onClick={(e) => {
          e.stopPropagation(); // Prevent navigation when clicking unlike
          onRemove();
        }}
      >
        <X />
      </button>
    </div>
  );
};

const LikedList = () => {
  const { data, isLoading, error } = useUserLikes(null, null, 1, 10);
  const [likedList, setLikedList] = useState([]);
  const { mutate: unlikeItemMutate } = useUnlikeItem();

  useEffect(() => {
    if (data) {
      setLikedList(data.likes);
    }
  }, [data]);

  const handleUnlike = (id, item_type, item_id) => {
    unlikeItemMutate({ item_type, item_id });
    setLikedList((prev) => prev.filter((item) => item.id !== id));
  };

  const uniqueTypes = [...new Set(likedList.map((item) => item.item_type))];
  const groupedByType = uniqueTypes.map((type) => ({
    type,
    items: likedList.filter((item) => item.item_type === type),
  }));

  if (isLoading) return <p><Loading/></p>;
  if (error) return <p>Error loading liked items</p>;

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>My Liked List</h1>
      {groupedByType.map(({ type, items }) => (
        <div key={type} className={styles.typeSection}>
          <h2 className={styles.typeTitle}>
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </h2>
          <Swiper
            modules={[Navigation, Mousewheel]}
            navigation={{ prevEl: '.prev-btn', nextEl: '.next-btn' }}
            mousewheel={{ forceToAxis: true, invert: false, sensitivity: 1 }}
            spaceBetween={20}
            slidesPerView="auto"
            className={styles.swiper}
            breakpoints={{
              320: { slidesPerView: 3 },
              480: { slidesPerView: 3 },
              768: { slidesPerView: 5 },
              828: { slidesPerView: 5 },
              1024: { slidesPerView: 7 },
              2490: { slidesPerView: 7 },
            }}
          >
            {items.map((item) => (
              <SwiperSlide key={item.id}>
                <LikedListCard 
                  item={item} 
                  onRemove={() => handleUnlike(item.id, item.item_type, item.item_id)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ))}
      <ToastContainer />
    </div>
  );
};

export default LikedList;
